export const AI_TOOLS = {
  "product-title-generator": {
    slug: "product-title-generator",
    path: "/ai-tools/product-title-generator",
    name: "Product Title Generator",
    icon: "Type",
    endpoint: "/api/ai/title",
    resultKey: "titles",
    resultType: "list",
    promise: "Generate five keyword-rich, high-converting product titles from a short description.",
    metaTitle: "AI Product Title Generator for Marketplaces | Ecroply AI",
    metaDesc: "Generate keyword-rich e-commerce product titles for Meesho, Flipkart, Amazon and Shopsy with AI. Free, fast, no login.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Women's cotton anarkali kurta with dupatta, floral print", required: true },
      { name: "keywords", label: "Keywords to include (optional)", type: "text", placeholder: "cotton, festive, plus size" },
      { name: "marketplace", label: "Marketplace", type: "select", options: ["General", "Meesho", "Flipkart", "Amazon", "Shopsy"] },
    ],
  },
  "product-description-generator": {
    slug: "product-description-generator",
    path: "/ai-tools/product-description-generator",
    name: "Product Description Generator",
    icon: "AlignLeft",
    endpoint: "/api/ai/description",
    resultKey: "description",
    resultType: "text",
    promise: "Write a persuasive, well-structured product description in seconds.",
    metaTitle: "Product Description Generator - AI Listing Copy | Ecroply AI",
    metaDesc: "Generate marketplace-ready product descriptions with AI. Describe your product, get a draft you can edit and publish. Free, no login.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Stainless steel 1L insulated water bottle, keeps drinks cold 24h", required: true },
      { name: "features", label: "Key features (optional)", type: "textarea", placeholder: "leak-proof lid, BPA-free, 24h cold" },
      { name: "tone", label: "Tone", type: "select", options: ["Professional", "Friendly", "Premium", "Playful", "Minimal"] },
      { name: "marketplace", label: "Marketplace", type: "select", options: ["General", "Meesho", "Flipkart", "Amazon", "Shopsy"] },
    ],
  },
  "bullet-point-generator": {
    slug: "bullet-point-generator",
    path: "/ai-tools/bullet-point-generator",
    name: "Bullet / Feature Point Generator",
    icon: "ListChecks",
    endpoint: "/api/ai/bullets",
    resultKey: "bullets",
    resultType: "list",
    promise: "Turn product details into six crisp, benefit-led feature bullets.",
    metaTitle: "AI Bullet Point & Feature Generator | Ecroply AI",
    metaDesc: "Generate benefit-led feature bullet points for your product listings with AI. Free and fast.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Wireless earbuds with 40h battery and ENC mic", required: true },
      { name: "details", label: "Details & specs (optional)", type: "textarea", placeholder: "IPX5, Bluetooth 5.3, touch controls" },
    ],
  },
  "seo-keyword-generator": {
    slug: "seo-keyword-generator",
    path: "/ai-tools/seo-keyword-generator",
    name: "SEO Keyword Generator",
    icon: "Search",
    endpoint: "/api/ai/keywords",
    resultKey: "keywords",
    resultType: "list",
    promise: "Get fifteen relevant search keywords buyers actually use.",
    metaTitle: "AI SEO Keyword Generator for E-commerce | Ecroply AI",
    metaDesc: "Discover the search keywords buyers use for your product with AI. Free keyword ideas for marketplace listings.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Men's running shoes, lightweight, breathable mesh", required: true },
      { name: "category", label: "Category (optional)", type: "text", placeholder: "Footwear / Sports shoes" },
    ],
  },
  "product-listing-assistant": {
    slug: "product-listing-assistant",
    path: "/ai-tools/product-listing-assistant",
    name: "Product Listing Assistant",
    icon: "Sparkles",
    endpoint: "/api/ai/listing",
    resultKey: null,
    resultType: "listing",
    promise: "Get a full listing — title, description, bullets and keywords — from a few inputs.",
    metaTitle: "AI Product Listing Assistant — Full Listing in One Click | Ecroply AI",
    metaDesc: "Generate a complete marketplace listing — title, description, bullets and SEO keywords — with AI. Free, no login.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Handmade scented soy candle, lavender, 200g", required: true },
      { name: "category", label: "Category (optional)", type: "text", placeholder: "Home fragrance / Candles" },
      { name: "features", label: "Key features (optional)", type: "textarea", placeholder: "40h burn, natural wax, reusable jar" },
      { name: "marketplace", label: "Marketplace", type: "select", options: ["General", "Meesho", "Flipkart", "Amazon", "Shopsy"] },
    ],
  },
  "amazon-product-title-generator": {
    slug: "amazon-product-title-generator",
    path: "/ai-tools/amazon-product-title-generator",
    name: "Amazon Product Title Generator",
    icon: "Type",
    endpoint: "/api/ai/title",
    resultKey: "titles",
    resultType: "list",
    promise: "Draft Amazon-ready product titles that follow Amazon's structure and length conventions.",
    metaTitle: "Amazon Product Title Generator - AI Listing Titles | Ecroply AI",
    metaDesc: "Generate Amazon-ready product titles with AI, built around Amazon's title structure and length limits. Free, editable drafts, no login.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Stainless steel 1L insulated water bottle, keeps drinks cold 24h", required: true },
      { name: "keywords", label: "Key attributes to include (size, colour, material, quantity)", type: "text", placeholder: "1L, matte black, BPA-free, pack of 2" },
      { name: "marketplace", label: "Marketplace", type: "select", options: ["Amazon", "General", "Meesho", "Flipkart", "Shopsy"] },
    ],
    faq: [
      { q: "What's the character limit for Amazon titles?", a: "It varies by category, generally in the 150-200 character range, with some categories capped lower. Amazon's current seller guidelines list the exact limit for your category." },
      { q: "Will this avoid Amazon's keyword-stuffing policy automatically?", a: "No — the draft is a starting point. Remove repeated keywords and promotional words before publishing, since Amazon can suppress or remove listings for non-compliant titles." },
      { q: "Can I use this for variations (different sizes/colors) in bulk?", a: "Generate one title per variation by changing the attribute input each time; there's no bulk/batch mode." },
      { q: "Is this a different tool from the general Product Title Generator?", a: "It's the same underlying generator with Amazon-specific guidance on this page. The generic version is also available if you're not marketplace-specific." },
    ],
    jsonLd: [{
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Amazon Product Title Generator",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Any (browser-based, AI-assisted)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
      url: "https://ecroplyai.in/ai-tools/amazon-product-title-generator",
    }],
    content: {
      intro: [
        "Amazon titles follow a fairly specific structure — brand, then product type, then key attributes like size, color or quantity — and most categories cap titles around 150-200 characters, with some stricter limits by category.",
        "This page uses the same AI title generator as the rest of the site, with input guidance tuned to Amazon's conventions, so the draft you get is already close to Amazon's expected format. As with any AI draft, review it against your category's specific title requirements before publishing — Amazon suspends listings for keyword-stuffed or non-compliant titles.",
      ],
      howTo: [
        "Enter your brand name, product type, and key attributes (size, color, material, quantity).",
        "Mention your product category if it has known title restrictions.",
        "Generate a draft title.",
        "Trim or reorder it to match your category's exact character limit.",
        "Cross-check against Amazon's current title guidelines for your category before publishing.",
      ],
      links: [
        { label: "Meesho Product Title Generator", path: "/ai-tools/meesho-product-title-generator" },
        { label: "Product Title Generator", path: "/ai-tools/product-title-generator" },
        { label: "Product Description Generator", path: "/ai-tools/product-description-generator" },
        { label: "SEO Keyword Generator", path: "/ai-tools/seo-keyword-generator" },
        { label: "Guide: How to Use the AI Listing Tools", path: "/blog/how-to-use-ai-listing-tools" },
      ],
    },
  },
  "meesho-product-title-generator": {
    slug: "meesho-product-title-generator",
    path: "/ai-tools/meesho-product-title-generator",
    name: "Meesho Product Title Generator",
    icon: "Type",
    endpoint: "/api/ai/title",
    resultKey: "titles",
    resultType: "list",
    promise: "Draft Meesho product titles in the descriptive, search-friendly style Meesho buyers respond to.",
    metaTitle: "Meesho Product Title Generator - AI Listing Titles | Ecroply AI",
    metaDesc: "Generate Meesho-ready product titles with AI, built around Meesho's search-friendly title style. Free, editable drafts, no login required.",
    fields: [
      { name: "product", label: "What are you selling?", type: "textarea", placeholder: "e.g. Women's cotton anarkali kurta with dupatta, floral print", required: true },
      { name: "keywords", label: "Details to include (material, style, use case)", type: "text", placeholder: "cotton, festive, women's ethnic wear" },
      { name: "marketplace", label: "Marketplace", type: "select", options: ["Meesho", "General", "Amazon", "Flipkart", "Shopsy"] },
    ],
    faq: [
      { q: "Is there a character limit for Meesho titles?", a: "Meesho doesn't publish as strict a limit as Amazon, but very long titles still get truncated in search results and app views, so keep the most important details near the front." },
      { q: "Should I include price or offer words in the title?", a: "No — avoid promotional language like \"sale\" or \"lowest price\" in the title itself; put pricing information in the price field instead." },
      { q: "Can I generate titles for product variants in bulk?", a: "No bulk mode — generate one title per variant by adjusting the input each time." },
      { q: "Is this different from the generic Product Title Generator?", a: "Same underlying generator, with Meesho-specific guidance built into this page's inputs and suggestions." },
    ],
    jsonLd: [{
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Meesho Product Title Generator",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Any (browser-based, AI-assisted)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
      url: "https://ecroplyai.in/ai-tools/meesho-product-title-generator",
    }],
    content: {
      intro: [
        "Meesho titles tend to work better when they're descriptive and search-friendly — including material, use case, and style details that buyers actually search for — rather than the strict attribute-first format Amazon expects.",
        "This page uses the same AI title generator as the rest of the site, with input guidance tuned to Meesho's conventions. Review the draft before publishing; Meesho, like other marketplaces, can restrict listings with irrelevant or repeated keywords.",
      ],
      howTo: [
        "Enter your product type and key details (material, style, use case, size/color if relevant).",
        "Mention your target buyer if useful (e.g. \"women's ethnic wear\").",
        "Generate a draft title.",
        "Edit for accuracy and remove anything that doesn't describe the actual product.",
        "Publish through the Meesho Seller Panel.",
      ],
      links: [
        { label: "Amazon Product Title Generator", path: "/ai-tools/amazon-product-title-generator" },
        { label: "Product Title Generator", path: "/ai-tools/product-title-generator" },
        { label: "Product Description Generator", path: "/ai-tools/product-description-generator" },
        { label: "SEO Keyword Generator", path: "/ai-tools/seo-keyword-generator" },
        { label: "Guide: How to Use the AI Listing Tools", path: "/blog/how-to-use-ai-listing-tools" },
      ],
    },
  },
};

export const AI_TOOL_LIST = Object.values(AI_TOOLS);
